import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Plus, Mail, Loader2, Send, CalendarClock, FileEdit, Trash2, RefreshCcw, AlertTriangle, RotateCcw, Repeat2 } from 'lucide-react';
import { dmApi, emailLogsApi } from '../lib/directMessagesApi';
import ComposerDrawer from '../components/direct-messages/ComposerDrawer';

const TABS = ['direct_messages', 'logs'];

// ─── Branded confirmation modal ────────────────────────────────
// Replaces window.confirm() for destructive actions so the prompt
// matches the curator-OS aesthetic (monochrome + danger accent).
function ConfirmModal({ open, title, body, confirmLabel, busy, onConfirm, onCancel }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={onCancel}
          data-testid="ec-confirm-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.2, 0.7, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[440px] bg-[#070707] border border-[#1a1a1a]"
          >
            <div className="p-5 flex items-start gap-3 border-b border-[#1a1a1a]">
              <div className="w-9 h-9 border border-[rgba(232,139,160,0.45)] bg-[rgba(232,139,160,0.06)] flex items-center justify-center shrink-0">
                <AlertTriangle className="w-4 h-4 text-[#e88ba0]" />
              </div>
              <div className="min-w-0">
                <h3 className="text-[13px] font-outfit text-white tracking-tight">{title}</h3>
                <p className="mt-1 text-[11px] font-mono text-[#888] leading-relaxed">{body}</p>
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2">
              <button
                onClick={onCancel}
                disabled={busy}
                data-testid="ec-confirm-cancel"
                className="text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444] disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={busy}
                data-testid="ec-confirm-ok"
                className="text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2 border border-[rgba(232,139,160,0.55)] text-[#e88ba0] hover:bg-[rgba(232,139,160,0.08)] inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StatusChip({ status }) {
  const palette = {
    draft: { color: '#aaa', border: '#333', bg: 'transparent' },
    queued: { color: '#90E0EF', border: 'rgba(144,224,239,0.45)', bg: 'rgba(144,224,239,0.06)' },
    sending: { color: '#90E0EF', border: 'rgba(144,224,239,0.45)', bg: 'rgba(144,224,239,0.06)' },
    scheduled: { color: '#e4c07a', border: 'rgba(228,192,122,0.45)', bg: 'rgba(228,192,122,0.06)' },
    sent: { color: '#7ed4a0', border: 'rgba(126,212,160,0.45)', bg: 'rgba(126,212,160,0.06)' },
    partial: { color: '#e4c07a', border: 'rgba(228,192,122,0.45)', bg: 'rgba(228,192,122,0.06)' },
    failed: { color: '#e88ba0', border: 'rgba(232,139,160,0.45)', bg: 'rgba(232,139,160,0.06)' },
  };
  const p = palette[status] || palette.draft;
  return (
    <span
      className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 border"
      style={{ color: p.color, borderColor: p.border, background: p.bg }}
    >
      {status || 'draft'}
    </span>
  );
}

function ProgressBar({ summary }) {
  if (!summary) return null;
  const total = summary.total || 0;
  const done = (summary.sent || 0) + (summary.failed || 0) + (summary.skipped || 0);
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className="mt-2">
      <div className="h-1 bg-[#111] overflow-hidden">
        <motion.div
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.4 }}
          className="h-full bg-[#90E0EF]"
          style={{ boxShadow: '0 0 8px rgba(144,224,239,0.4)' }}
        />
      </div>
      <div className="mt-1 text-[10px] font-mono text-[#666] flex flex-wrap items-center gap-3">
        <span>{done}/{total}</span>
        <span className="text-[#7ed4a0]">sent {summary.sent || 0}</span>
        {summary.failed > 0 && <span className="text-[#e88ba0]">failed {summary.failed}</span>}
        {summary.skipped > 0 && <span className="text-[#888]">skipped {summary.skipped}</span>}
      </div>
    </div>
  );
}

export default function EmailConsolePage() {
  const { exhibitionId } = useOutletContext();
  const [tab, setTab] = useState('direct_messages');
  const [campaigns, setCampaigns] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  // ─── Confirm modal state ────────────────────────────────────
  // Holds the campaign pending archive while we ask "Are you sure?".
  // Branded modal replaces window.confirm() so the prompt matches the
  // curator-OS aesthetic and so we control the click semantics (no
  // stale row-click bubbling).
  const [confirmTarget, setConfirmTarget] = useState(null); // {id, name}
  const [confirmBusy, setConfirmBusy] = useState(false);

  const fetchCampaigns = async () => {
    if (!exhibitionId) return;
    try {
      const data = await dmApi.list(exhibitionId);
      setCampaigns(data);
    } catch {
      /* silent */
    }
  };

  const fetchLogs = async () => {
    if (!exhibitionId) return;
    try {
      const data = await emailLogsApi.list(exhibitionId);
      setLogs(data);
    } catch {
      /* silent */
    }
  };

  // Derived loading flag — flipped via setTimeout so the strict
  // react-hooks/set-state-in-effect rule doesn't fire.
  const setLoadingDeferred = (next) => {
    setTimeout(() => setLoading(next), 0);
  };

  useEffect(() => {
    if (!exhibitionId) return undefined;
    let cancelled = false;
    setLoadingDeferred(true);
    (async () => {
      await Promise.all([fetchCampaigns(), fetchLogs()]);
      if (!cancelled) setLoadingDeferred(false);
    })();
    return () => { cancelled = true; };
  }, [exhibitionId]);

  // Poll campaigns while any is queued/sending so progress bar moves live.
  useEffect(() => {
    const anyActive = campaigns.some((c) => c.status === 'queued' || c.status === 'sending');
    if (!anyActive) return undefined;
    const t = setInterval(fetchCampaigns, 4000);
    return () => clearInterval(t);
  }, [campaigns]);

  const openComposer = (id = null) => {
    setActiveId(id);
    setDrawerOpen(true);
  };

  const closeComposer = () => {
    setDrawerOpen(false);
    setActiveId(null);
  };

  const handleArchive = async () => {
    if (!confirmTarget) return;
    setConfirmBusy(true);
    try {
      await dmApi.archive(confirmTarget.id);
      toast.success('Archived');
      await fetchCampaigns();
      setConfirmTarget(null);
    } catch {
      toast.error('Archive failed');
    } finally {
      setConfirmBusy(false);
    }
  };

  // Open the confirm dialog. Always stops propagation so the row's
  // click handler (which opens the composer) doesn't ALSO fire.
  const askArchive = (campaign, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setConfirmTarget({ id: campaign.id, name: campaign.name || 'Untitled' });
  };

  // Manual unstick for campaigns wedged in 'sending'/'queued'/'failed'.
  // Should rarely be needed thanks to send_campaign's top-level
  // try/finally + the scheduler's stuck-recovery pass, but the curator
  // gets an immediate escape hatch instead of waiting for the next tick.
  const handleResetStuck = async (campaign, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!window.confirm(`Reset “${campaign.name || 'Untitled'}” back to draft? This is for campaigns that look stuck in '${campaign.status}'.`)) {
      return;
    }
    try {
      await dmApi.reset(campaign.id);
      toast.success(`Reset · ${campaign.status} → draft`);
      await fetchCampaigns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    }
  };

  // Retry-failed: re-dispatch only the addresses that failed the last
  // send. Reuses the existing locked snapshot, narrowed down to the
  // failed subset, so the curator never has to re-confirm or
  // re-create the campaign just because Resend rate-limited a few.
  const handleRetryFailed = async (campaign, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const failedCount = campaign?.send_summary?.failed || 0;
    if (!failedCount) return;
    if (!window.confirm(`Retry the ${failedCount} failed recipient${failedCount === 1 ? '' : 's'} from “${campaign.name || 'Untitled'}”?`)) {
      return;
    }
    try {
      const res = await dmApi.retryFailed(campaign.id);
      toast.success(`Re-queued · ${res.retry_count} recipient${res.retry_count === 1 ? '' : 's'}`);
      await fetchCampaigns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed');
    }
  };

  // Edit action — used both by row click AND by the explicit pencil
  // icon. Stops propagation when fired from the icon so we don't get
  // a double-fire if the bubble path traverses the row.
  const handleEdit = (campaignId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    openComposer(campaignId);
  };

  const handleSaved = async () => {
    await fetchCampaigns();
  };

  const sortedCampaigns = useMemo(() => {
    return [...campaigns].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
  }, [campaigns]);

  return (
    <div className="space-y-6" data-testid="email-console-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-outfit text-white tracking-tight">Email Console</h1>
          <p className="text-xs font-mono text-[#888] mt-1">Compose branded direct messages, target your audience, schedule & send.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { fetchCampaigns(); fetchLogs(); }}
            className="text-[10px] font-mono px-3 py-2 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444] inline-flex items-center gap-1.5"
            data-testid="ec-refresh"
          >
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
          <button
            onClick={() => openComposer(null)}
            data-testid="ec-create-direct-message"
            className="text-[11px] font-mono px-4 py-2 bg-[#90E0EF] text-black hover:bg-[#a8e8f4] inline-flex items-center gap-1.5 uppercase tracking-[0.18em]"
          >
            <Plus className="w-3 h-3" /> Create Direct Message
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-[#1a1a1a] pb-2">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`ec-tab-${t}`}
            className={`text-[10px] font-mono px-3 py-1.5 border uppercase tracking-[0.18em] transition-all ${
              tab === t
                ? 'border-[#90E0EF] text-[#90E0EF] bg-[#90E0EF]/10'
                : 'border-[#222] text-[#777] hover:text-white hover:border-[#333]'
            }`}
          >
            {t === 'direct_messages' ? 'Direct Messages' : 'Logs'}
          </button>
        ))}
      </div>

      {tab === 'direct_messages' && (
        <div className="border border-[#1a1a1a]">
          <div className="grid grid-cols-12 text-[10px] font-mono uppercase tracking-[0.18em] text-[#666] px-3 py-2 border-b border-[#1a1a1a]">
            <div className="col-span-4">Name</div>
            <div className="col-span-3">Subject</div>
            <div className="col-span-1">Audience</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2">Progress</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {loading ? (
            <div className="p-8 text-center text-[#444] text-xs font-mono">Loading…</div>
          ) : sortedCampaigns.length === 0 ? (
            <div className="p-12 text-center" data-testid="ec-empty-state">
              <Mail className="w-8 h-8 mx-auto text-[#333]" />
              <p className="mt-3 text-xs font-mono text-[#666]">No direct messages yet.</p>
              <button
                onClick={() => openComposer(null)}
                className="mt-4 text-[10px] font-mono px-4 py-2 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 inline-flex items-center gap-1.5 uppercase tracking-[0.18em]"
              >
                <Plus className="w-3 h-3" /> Create the first one
              </button>
            </div>
          ) : (
            sortedCampaigns.map((c) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => openComposer(c.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openComposer(c.id);
                  }
                }}
                data-testid={`ec-row-${c.id}`}
                className="group w-full text-left grid grid-cols-12 items-start gap-2 px-3 py-3 border-b border-[#111] hover:bg-[#0d0d0d] focus:bg-[#0d0d0d] focus:outline-none cursor-pointer transition-colors"
              >
                <div className="col-span-4 min-w-0">
                  <div className="text-xs font-mono text-white truncate">{c.name || 'Untitled'}</div>
                  <div className="text-[10px] font-mono text-[#555] mt-0.5">{new Date(c.updated_at || c.created_at).toLocaleString()}</div>
                </div>
                <div className="col-span-3 text-[11px] font-mono text-[#aaa] truncate">{c.subject || '—'}</div>
                <div className="col-span-1 text-[10px] font-mono text-[#999] uppercase tracking-[0.12em]">
                  {c.audience_type}
                  {c.confirmed_audience && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-[rgba(126,212,160,0.45)] text-[#7ed4a0] normal-case tracking-normal text-[9px]"
                      title={`Locked · confirmed ${new Date(c.confirmed_audience.confirmed_at).toLocaleString()}`}
                      data-testid={`ec-row-locked-${c.id}`}
                    >
                      🔒 {c.confirmed_audience.count}
                    </div>
                  )}
                </div>
                <div className="col-span-1">
                  <StatusChip status={c.status} />
                </div>
                <div className="col-span-2">
                  {c.send_summary ? (
                    <ProgressBar summary={c.send_summary} />
                  ) : (
                    <span className="text-[10px] font-mono text-[#444]">—</span>
                  )}
                  {c.scheduled_for && !c.send_summary && (
                    <div className="text-[10px] font-mono text-[#e4c07a] mt-1 inline-flex items-center gap-1">
                      <CalendarClock className="w-3 h-3" /> {new Date(c.scheduled_for).toLocaleString()}
                    </div>
                  )}
                </div>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  {(c.send_summary?.failed || 0) > 0 && c.status !== 'sending' && c.status !== 'queued' && (
                    <button
                      type="button"
                      onClick={(e) => handleRetryFailed(c, e)}
                      data-testid={`ec-row-retry-${c.id}`}
                      className="opacity-80 group-hover:opacity-100 text-[#90E0EF] hover:text-[#a8e8f4] p-1.5 border border-[rgba(144,224,239,0.4)] hover:bg-[rgba(144,224,239,0.06)] transition-all"
                      title={`Retry ${c.send_summary.failed} failed recipient${c.send_summary.failed === 1 ? '' : 's'}`}
                    >
                      <Repeat2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(c.status === 'sending' || c.status === 'queued' || c.status === 'failed') && (
                    <button
                      type="button"
                      onClick={(e) => handleResetStuck(c, e)}
                      data-testid={`ec-row-reset-${c.id}`}
                      className="opacity-80 group-hover:opacity-100 text-[#e4c07a] hover:text-[#f4cf85] p-1.5 border border-[rgba(228,192,122,0.4)] hover:bg-[rgba(228,192,122,0.06)] transition-all"
                      title={`Reset · stuck in ${c.status}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => handleEdit(c.id, e)}
                    data-testid={`ec-row-edit-${c.id}`}
                    className="opacity-60 group-hover:opacity-100 text-[#777] hover:text-[#90E0EF] p-1.5 border border-transparent hover:border-[rgba(144,224,239,0.4)] transition-all"
                    title="Edit"
                  >
                    <FileEdit className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => askArchive(c, e)}
                    data-testid={`ec-row-delete-${c.id}`}
                    className="opacity-60 group-hover:opacity-100 text-[#777] hover:text-[#e88ba0] p-1.5 border border-transparent hover:border-[rgba(232,139,160,0.4)] transition-all"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'logs' && (
        <div className="border border-[#1a1a1a] overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-[#1a1a1a] text-[#666] uppercase tracking-[0.15em]">
                <th className="text-left p-3">Sent</th>
                <th className="text-left p-3">Recipient</th>
                <th className="text-left p-3">Subject</th>
                <th className="text-left p-3">Campaign / Template</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-[#444]">Loading…</td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-12 text-center text-[#666]">
                    <Send className="w-6 h-6 mx-auto text-[#333]" />
                    <p className="mt-2">No emails sent yet.</p>
                  </td>
                </tr>
              ) : (
                logs.map((l) => (
                  <tr key={l.id} className="border-b border-[#111]" data-testid={`ec-log-${l.id}`}>
                    <td className="p-3 text-[#666]">{l.sent_at ? new Date(l.sent_at).toLocaleString() : '—'}</td>
                    <td className="p-3 text-[#ddd]">
                      <div>{l.recipient_email}</div>
                      {l.recipient_name && <div className="text-[10px] text-[#666]">{l.recipient_name}</div>}
                    </td>
                    <td className="p-3 text-[#aaa] truncate">{l.subject}</td>
                    <td className="p-3 text-[#888] text-[10px]">
                      {(l.template_key || '').startsWith('dm:') ? (
                        <span className="px-1.5 py-0.5 border border-[#90E0EF]/40 text-[#90E0EF] uppercase tracking-[0.16em]">Direct Msg</span>
                      ) : (
                        l.template_key || '—'
                      )}
                    </td>
                    <td className="p-3">
                      <StatusChip status={l.status} />
                      {l.error_message && (
                        <div className="text-[10px] text-[#e88ba0] mt-0.5 truncate max-w-[260px]">{l.error_message}</div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <ComposerDrawer
        open={drawerOpen}
        exhibitionId={exhibitionId}
        campaignId={activeId}
        onClose={closeComposer}
        onSaved={handleSaved}
      />

      <ConfirmModal
        open={!!confirmTarget}
        title="Delete this direct message?"
        body={
          confirmTarget
            ? `“${confirmTarget.name}” will be archived. Sent recipients keep their delivery history, but the campaign will disappear from this list. This cannot be undone from the UI.`
            : ''
        }
        confirmLabel="Yes, delete"
        busy={confirmBusy}
        onConfirm={handleArchive}
        onCancel={() => (confirmBusy ? null : setConfirmTarget(null))}
      />
    </div>
  );
}
